export interface TextAutocompletePanelPosition {
    top: number;
    left: number;
    width: number;
}

/**
 * Calculates the position of the autocomplete panel relative to the caret position inside the given input/textarea element.
 * TODO: Support above and auto positioning of the dropdown panel.
 */
export function getTextAutocompletePanelPosition(inputEl: HTMLInputElement | HTMLTextAreaElement, caretIndex: number, containerSelector: string): TextAutocompletePanelPosition {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!inputEl) return;

    // Calculate the caret position
    const rect = getInputCaretClientRect(inputEl, caretIndex);
    if (!rect) return;

    const container = inputEl.closest(containerSelector) as HTMLElement | null;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const inputRect = inputEl.getBoundingClientRect();

    const inputWrapper = inputEl.closest('.mat-form-field-wrapper') as HTMLElement;
    const width = inputWrapper ? Math.max(150, inputWrapper.getBoundingClientRect().width - 24) : Math.max(150, inputRect.width); // Fallback

    // caret rect is in viewport coordinates; convert to container-relative.
    const top = (rect.bottom - containerRect.top) + 8;
    const left = (inputRect.left - containerRect.left);

    const position: TextAutocompletePanelPosition = {top, left, width};
    return position;
}

function parseLineHeight(computed: CSSStyleDeclaration): number {
    const lh = computed.lineHeight;
    if (lh === 'normal' || !lh) {
        const fontSize = parseFloat(computed.fontSize || '16') || 16;
        return fontSize * 1.2;
    }
    return parseFloat(lh) || 16;
}

function applyMirrorLayoutStyles(mirror: HTMLDivElement, source: CSSStyleDeclaration): void {
    mirror.style.font = source.font;
    mirror.style.padding = source.padding;
    mirror.style.border = source.border;
    mirror.style.boxSizing = source.boxSizing;
    mirror.style.whiteSpace = source.whiteSpace;
    mirror.style.lineHeight = source.lineHeight;
    mirror.style.letterSpacing = source.letterSpacing;
    mirror.style.wordBreak = source.wordBreak;
    mirror.style.overflowWrap = source.overflowWrap;
    mirror.style.width = source.width;

    mirror.classList.add('mydocma-caret-mirror');
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordWrap = 'break-word';
}

/**
 * 1-based visual line index for `caretIndex`, matching browser layout via a hidden mirror element.
 */
function getCaretVisualLine(inputEl: HTMLInputElement | HTMLTextAreaElement, caretIndex: number): number {
    const value = inputEl.value || '';
    const caret = Math.max(0, Math.min(caretIndex, value.length));
    const computed = window.getComputedStyle(inputEl);

    const mirror = document.createElement('div');
    applyMirrorLayoutStyles(mirror, computed);

    const before = value.substring(0, caret);
    const after = value.substring(caret);
    if (before) {
        mirror.appendChild(document.createTextNode(before));
    }
    const marker = document.createElement('span');
    marker.textContent = '|';
    mirror.appendChild(marker);
    if (after) {
        mirror.appendChild(document.createTextNode(after));
    }

    document.body.appendChild(mirror);
    const lineHeight = parseLineHeight(computed);
    const visualLine = Math.floor(marker.offsetTop / lineHeight) + 1;
    document.body.removeChild(mirror);

    return Math.max(1, visualLine);
}

function getInputCaretClientRect(inputEl: HTMLInputElement | HTMLTextAreaElement, caretIndex: number): DOMRect {
    if (typeof window === 'undefined' || typeof document === 'undefined') return null;

    if (!inputEl) return null;

    const computed = window.getComputedStyle(inputEl);
    const inputRect = inputEl.getBoundingClientRect();

    const paddingLeft = parseFloat(computed.paddingLeft || '0') || 0;
    const lineHeight = parseLineHeight(computed);
    const visualLine = getCaretVisualLine(inputEl, caretIndex);

    const top = inputRect.top - lineHeight + visualLine * lineHeight - (inputEl.scrollTop || 0);
    const left = inputRect.left + paddingLeft - (inputEl.scrollLeft || 0);

    return new DOMRect(left, top, 1, lineHeight);
}
